/**
 * @fileoverview AR-670 — shapes of the read-only backend contract (branch
 * feat-classroom-link-only-courses, `docs/frontend-integration/
 * google-classroom-assignments.md`): legacy assignment state, the teacher's
 * submissions import and the student's own stream files. Assignment CREATION
 * was removed from the backend — nothing here triggers writes to Google.
 */

import { SchoolConnectionStatus } from './types'

/**
 * One row of GET /api/arc_mosaico/google_classroom_courses — an existing
 * project↔course link, for the "Linked courses" table. Managers/managing
 * tutors see the whole school; a regular tutor sees only links backed by
 * their own connection. Local data only, so it works even when the
 * connection needs reconnection.
 */
export interface LinkedCourseRow {
  id: string
  googleCourseId: string
  name: string
  alternateLink: string | null
  origin: 'created' | 'linked' | null
  syncEnabled: boolean
  mappedStudentsCount: number
  lastRosterSyncAt: string | null
  /** Status of the user connection backing this link. 'disconnected' means
   *  the tutor who linked disconnected — the link survives but stops syncing. */
  connectionStatus: SchoolConnectionStatus
  /** Google account backing the link — identifies the owning tutor. Null
   *  when the connection is gone. */
  connectionGoogleEmail: string | null
  /** Class section name (project.group_school_term). */
  groupName: string | null
  project: { id: string; name: string; status: string }
}

/**
 * classroom_assignment object on studentTask / task_track deliverable.
 * LEGACY ONLY — assignments created before the read-only migration. New tasks
 * always come with classroom_assignment: null, and `pending` never resolves
 * anymore (the creation worker was deleted).
 */
export interface ClassroomAssignmentState {
  status: 'pending' | 'created' | 'failed'
  alternateLink: string | null
  /** 'not_course_teacher' | 'google_api_error_<n>' */
  failureReason: string | null
  lastSubmissionsSyncAt: string | null
}

/** Per-assignment summary of POST sync_submissions (teacher on-demand sync). */
export interface SubmissionsSyncSummary {
  studentTaskId: string
  title: string
  summary: {
    imported: number
    updated: number
    /** Reruns that found nothing new — never counted by the refresh toast. */
    unchanged: number
    skippedNotSubmitted: number
    skippedUnmapped: Array<{ googleUserId: string }>
    attachmentsSkipped: number
    filesDownloaded: number
  }
}

/**
 * `missing_announcements_scope`: the connection behind the link predates the
 * classroom.announcements.readonly scope — the tutor who linked must
 * reconnect their own account.
 */
export type StudentClassroomFilesUnavailableReason =
  | 'project_not_linked'
  | 'student_not_mapped'
  | 'school_not_connected'
  | 'missing_announcements_scope'

/** One file the student posted on the course stream (announcements). */
export interface StudentClassroomFile {
  kind: 'drive_file' | 'youtube' | 'link' | 'form'
  name: string
  url: string
  iconUrl: string | null
  /** creationTime of the announcement — tells same-named files apart. */
  postedAt: string | null
}

/**
 * GET /api/student_tasks/:id/google_classroom_files — the files the student
 * THEMSELVES posted on the linked course stream. Unavailable states come back
 * as 200 {available:false, reason}, never as errors.
 */
export interface StudentClassroomFiles {
  available: boolean
  reason?: StudentClassroomFilesUnavailableReason
  files: StudentClassroomFile[]
}

